import { api } from "@/lib/api";
import type { SiteTask } from "@/lib/workflow";

export interface Activity {
  id: string;
  naziv: string;
  aktivna: boolean;
  redoslijed: number;
  vrsta: "izdvojivo" | "gubitak";
}
export interface AllocationItem {
  takt_id?: string | null;
  sporedna_aktivnost_id?: string | null;
  minute: number;
}
export interface WorkerAllocation {
  evidencija_sati_id: string;
  radnik_id: string;
  radnik: string;
  neto_minute: number;
  zadani_takt_id: string | null;
  potvrdjena: boolean;
  treba_raspodjelu: boolean;
  stavke: AllocationItem[];
}
export interface AllocationDay {
  zakljucan: boolean;
  radnici: WorkerAllocation[];
  taktovi: SiteTask[];
}
export interface Performance {
  ukupno: string;
  na_taktovima: string;
  sporedno: string;
  nerasporedjeno: string;
  taktovi: { id: string; naziv: string; plan: string | null; utroseno: string; razlika: string | null; status: string }[];
  aktivnosti: { id: string; naziv: string; vrsta: Activity["vrsta"]; sati: string }[];
  brigade: { id: string | null; naziv: string; sati: string }[];
  poslovodje: { id: string | null; naziv: string; sati: string }[];
}

export function defaultAllocations(workers: WorkerAllocation[]): WorkerAllocation[] {
  return workers.map((worker) => ({ ...worker, stavke: worker.potvrdjena ? worker.stavke.map((item) => ({ ...item })) : worker.zadani_takt_id ? [{ takt_id: worker.zadani_takt_id, minute: worker.neto_minute }] : [] }));
}
export const allocationTotal = (worker: WorkerAllocation) => worker.stavke.reduce((sum, item) => sum + item.minute, 0);
export const invalidWorkers = (workers: WorkerAllocation[]) => workers.filter((worker) => allocationTotal(worker) !== worker.neto_minute || worker.stavke.some((item) => !Number.isInteger(item.minute) || item.minute <= 0));
export const displayHours = (minutes: number) => new Intl.NumberFormat("hr-HR", { maximumFractionDigits: 2 }).format(minutes / 60);

export function allocate(workers: WorkerAllocation[], selected: string[], target: Omit<AllocationItem, "minute">, minutes: number): WorkerAllocation[] {
  if (!Number.isInteger(minutes) || minutes <= 0) throw new Error("Unesite pozitivan broj minuta.");
  if (!selected.length) throw new Error("Odaberite barem jednog radnika.");
  return workers.map((worker) => {
    if (!selected.includes(worker.evidencija_sati_id)) return worker;
    const items = worker.stavke.map((item) => ({ ...item }));
    const free = Math.max(0, worker.neto_minute - allocationTotal(worker));
    const primary = items.find((item) => item.takt_id && item.takt_id === worker.zadani_takt_id) ?? items.find((item) => item.takt_id);
    const deduct = Math.max(0, minutes - free);
    if (minutes > worker.neto_minute || deduct > (primary?.minute ?? 0)) throw new Error(`Za ${worker.radnik} nema dovoljno sati na taktu. Smanjite trajanje ili vratite zadanu raspodjelu.`);
    if (primary) primary.minute -= deduct;
    const existing = items.find((item) => target.takt_id ? item.takt_id === target.takt_id : item.sporedna_aktivnost_id === target.sporedna_aktivnost_id);
    if (existing) existing.minute += minutes;
    else items.push({ ...target, minute: minutes });
    return { ...worker, potvrdjena: false, treba_raspodjelu: true, stavke: items.filter((item) => item.minute > 0) };
  });
}

export const allocations = {
  day: async (site: string, datum: string, signal?: AbortSignal) => (await api.get<AllocationDay>("/sati/raspodjela/dan", { params: { gradiliste_id: site, datum }, signal })).data,
  activities: async (signal?: AbortSignal) => (await api.get<Activity[]>("/sporedne-aktivnosti", { signal })).data,
  save: async (site: string, datum: string, workers: WorkerAllocation[]) => api.put("/sati/raspodjela/dan", { gradiliste_id: site, datum, radnici: workers.map((worker) => ({ radnik_id: worker.radnik_id, evidencija_sati_id: worker.evidencija_sati_id, stavke: worker.stavke })) }),
  report: async (site: string, od: string, end: string, crew: string, signal?: AbortSignal) => (await api.get<Performance>("/izvjestaji/ucinak-sati", { params: { gradiliste_id: site, od, do: end, brigada_id: crew || undefined }, signal })).data,
  excel: async (site: string, od: string, end: string, crew: string) => (await api.get<Blob>("/izvjestaji/ucinak-sati/excel", { params: { gradiliste_id: site, od, do: end, brigada_id: crew || undefined }, responseType: "blob" })).data,
  saveActivity: async (activity: Omit<Activity, "id">, id?: string) => id ? api.put(`/sporedne-aktivnosti/${id}`, activity) : api.post("/sporedne-aktivnosti", activity),
};
